"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.bridgePolygons = exports.insertVertex = exports.ringSlice = exports.geoJsonExtractLoop = exports.isWindingExternal = void 0;
const turf_1 = require("@turf/turf");
const lodash_1 = __importDefault(require("lodash"));
const unit_conversion_1 = require("../calculators/unit-conversion");
const polygon_distance_1 = require("../calculators/polygon-distance");
const MAX_BRIDGE_REDUCE_AREA_SQM = 100; // Revert the bridge operation if the combined area has shrunk. There is a large margin on this to avoid false positives from small intersects/overlaps
const ENABLE_ASSERT = false; // Enable some extra tests for debugging, but can impact performance a lot (and can generate false-positives)
const BRIDGE_WALK_MAX_WIDTH_M = 2.0; // When widening a bridge, the gap between the polygons must be less than this distance wide
/**
 * If ENABLE_ASSERT is true, this function will run a function and
 * throw an error if the function returns False.
 *
 * @param func Function to run to evaluate condition
 * @param message Message to display if assert fails
 * @param always Always run this assert regardless of the global ENABLE_ASSERT parameter
 */
function assert(func, message, always = false) {
    if (!ENABLE_ASSERT && !always) {
        return;
    }
    if (!func()) {
        throw new Error(message);
    }
}
/**
 * Checks to see if the winding order of a polygon is correct
 * This is a reasonably expensive function to run as it is implemented
 * in such a way that it computes the correct winding order.
 * There is clear room for improvement here.
 *
 * @param feature geojson feature to check
 * @returns true if the winding is correct.
 */
function isWindingExternal(feature) {
    return lodash_1.default.isEqual(feature, (0, turf_1.rewind)(feature));
}
exports.isWindingExternal = isWindingExternal;
/**
 * Returns true if the positions are strictly identical. Does NOT allow for
 * floating point precision
 *
 * @param point1 One point to compare
 * @param point2 The other point to compare
 * @returns true if the points are exactly identical
 */
function pointExactlyEqual(point1, point2) {
    return point1[0] === point2[0] && point1[1] === point2[1];
}
/**
 * Returns the index within a feature of a vertex at a particular set of coordinates
 *
 * @param feature Look inside this feature
 * @param position For a vertex at this position
 * @returns The ring and vertex ID's
 */
function findPolyLoopAndRingIndex(feature, position) {
    for (let ringIndex = 0; ringIndex < feature.geometry.coordinates.length; ringIndex += 1) {
        const pointArray = feature.geometry.coordinates[ringIndex];
        for (let pointIndex = 0; pointIndex < pointArray.length; pointIndex += 1) {
            const point = pointArray[pointIndex];
            if (pointExactlyEqual(point, position)) {
                return {
                    loopId: ringIndex,
                    vertId: pointIndex,
                };
            }
        }
    }
    return undefined;
}
/**
 * Linearly interpolate from a towards b.
 *
 * @param a Start
 * @param b End
 * @param percent How far to interpolate from start to end
 * @returns Somewhere between the start and end.
 */
function lerp(a, b, percent) {
    return a * (1.0 - percent) + b * percent;
}
/**
 * Linearly interpolate from a towards b.
 *
 * @param from Start at this location
 * @param towards Move towards this position
 * @param percent By this percentage
 * @returns A point between `from` and `towards` based on the percentage
 */
function positionLerp(from, towards, percent) {
    return [lerp(from[0], towards[0], percent), lerp(from[1], towards[1], percent)];
}
/**
 * Slide a vertex along a line between two points
 *
 * @param from Start at this location
 * @param towards Move towards this position
 * @param maxDistance By this distance, clamped by the `towards` position
 * @returns A point `maxDistance` away from `from` moved in the direction of `towards`
 */
function positionSlide(from, towards, maxDistance) {
    const distBetween = Math.sqrt((0, polygon_distance_1.distanceBetweenSquared)(from, towards));
    if (distBetween === 0) {
        return from;
    }
    const percent = Math.max(Math.min(maxDistance / distBetween, 1.0), 0.0);
    return positionLerp(from, towards, percent);
}
/**
 * Extract a loop from a geoJSON positional array.
 * Conceptually this is very similar to rotating an array, but it also deals
 * with geojson's arrays starting/ending with the same value.
 * For example some geoJSON may have a polygon loop that looks like:
 * [[0,0], [1,1], [2,1], [0,0]]
 *
 * This function allows you to create a new ring starting from the middle.
 * without the same start/end.
 * For example with the previous shape, an offset of 1 would return:
 * [[1,1], [2,1], [0,0]]
 *
 * @param array The array to operate on
 * @param offset Which element should become the new first element
 * @returns A loop of positions
 */
function geoJsonExtractLoop(array, offset) {
    return [...array.slice(offset, array.length - 1), ...array.slice(0, offset)];
}
exports.geoJsonExtractLoop = geoJsonExtractLoop;
/**
 * Subtract two vectors. For example it can convert from two positions into the
 * vector describing the cartesian distance between them
 *
 * @param pos1 Start position
 * @param pos2 End position
 * @returns Vector between pos1 and pos2
 */
function vecSubtract(pos1, pos2) {
    return [pos1[0] - pos2[0], pos1[1] - pos2[1]];
}
/**
 * Dot product
 *
 * @param vec1 One of the vectors
 * @param vec2 The other vector
 * @returns The dot product between the two vectors
 */
function vecDot(vec1, vec2) {
    return vec1[0] * vec2[0] + vec1[1] * vec2[1];
}
/**
 * @param x The number to wrap
 * @param length The size of the ring to wrap around
 * @returns x modulo length - using "clock arithmetic" for negative numbers
 */
function wrap(x, length) {
    return ((x % length) + length) % length;
}
/**
 * Slide a vertex towards or away from another vertex in order to ensure that a
 * bridge always has a positive width.
 * For more information see the documentation here:
 * https://carboncrop.atlassian.net/wiki/spaces/CAR/pages/137560111/Bridge+Width+on+Acute+Angles
 *
 * @param basePoint The point that we should slide from
 * @param bridgeOtherEnd The other end of the bridge
 * @param targetPoint Vertex to slide towards/away from
 * @param bridgeWidth The approximate width of the bridge
 * @returns The position to put the vertex at
 */
function calculateBridgeVertPosition(basePoint, bridgeOtherEnd, targetPoint, bridgeWidth) {
    const bridgeDirection = vecSubtract(basePoint, bridgeOtherEnd);
    const slideDirection = vecSubtract(basePoint, targetPoint);
    const perpendicularBridgeDirection = [-bridgeDirection[1], bridgeDirection[0]];
    const angleDir1 = vecDot(perpendicularBridgeDirection, slideDirection);
    let slideProportion = 0.0; // Asymmetric slide distances allow bridging at acute angles
    if (angleDir1 > 0) {
        if (bridgeWidth > 0) {
            slideProportion = 0.1;
        }
        else {
            slideProportion = 1.0;
        }
    }
    else if (bridgeWidth > 0) {
        slideProportion = 1.0;
    }
    else {
        slideProportion = 0.1;
    }
    const slideAmount = Math.abs(bridgeWidth) * slideProportion * 0.5;
    return positionSlide(basePoint, targetPoint, slideAmount);
}
/**
 * Like slice, but behaves well for rings. Takes into account that the first and last point
 * are equal, and also that the first and last point can cross over the start/end of the
 * coordinate array.
 *
 * @param loop The loop to return a slice of
 * @param start The start of the loop to keep, inclusive
 * @param end The end of the loop to keep, exclusive
 * @returns A slice of the input loop
 */
function ringSlice(loop, start, end) {
    if (loop.length === 0) {
        return [];
    }
    if (loop[0] !== loop[loop.length - 1]) {
        throw new Error('Start and end of the loop must have the same value');
    }
    const l = loop.length - 1;
    const startSafe = wrap(start, l);
    const endSafe = wrap(end, l);
    if (startSafe < endSafe) {
        return [...loop.slice(startSafe, endSafe), loop[startSafe]];
    }
    return [...loop.slice(startSafe, l), ...loop.slice(0, endSafe), loop[startSafe]];
}
exports.ringSlice = ringSlice;
/**
 * Tries to widen a bridge by walking along geomtry
 *
 * @param coords The input ring
 * @param bridgeStart The index of the point in the ring which represents the start of the bridge. Expects bridgeStart+1 to be the end
 * @param maxDistance The threshold under which we can walk the bridge
 * @returns A new set of coordinates.
 */
function walkBridge(coords, bridgeStart, maxDistance) {
    const length = coords.length - 1;
    let a = bridgeStart;
    let b = wrap(a + 1, length);
    const maxDistanceSq = maxDistance ** 2;
    const factors = (0, unit_conversion_1.degreesToMetresFactor)(coords[0]);
    for (;;) {
        const aP = wrap(a - 1, length);
        const bP = wrap(b + 1, length);
        if (aP === bP || a === aP || b === aP) {
            // We've come full circle and removed every point in the loop. Return no geometry
            return undefined;
        }
        const candidates = [
            [a, bP],
            [aP, b],
            [aP, bP],
        ];
        const candidatesWithDistance = candidates.map(([c1, c2]) => ({
            coords: [c1, c2],
            distSq: (0, polygon_distance_1.distanceBetweenSquaredScalingFactors)(coords[c1], coords[c2], factors),
        }));
        const bestCandidate = lodash_1.default.minBy(candidatesWithDistance, (c) => c.distSq);
        if (bestCandidate && bestCandidate.distSq < maxDistanceSq) {
            [a, b] = bestCandidate.coords;
        }
        else {
            break;
        }
    }
    return ringSlice(coords, b, a + 1);
}
/**
 * Creates a bridge between two polygon loops, returning a single point array as
 * an output
 *
 * @param pointArray1 One loop to bridge between
 * @param pointArray2 The other loop to bridge between
 * @param data1 The loop and vertex ID at which to place the bridge
 * @param data2 The loop and vertex ID at which to place the bridge
 * @param bridgeWidthMeters How wide the bridge should (approximately) be
 * @returns A single polygon loop describing the joined arrays.
 */
function bridgeLoops(pointArray1, pointArray2, data1, data2, bridgeWidthMeters) {
    const poly1 = (0, turf_1.polygon)([pointArray1]);
    const poly2 = (0, turf_1.polygon)([pointArray2]);
    const pointArray1Internal = data1.loopId !== 0; // True if the array a "hole" in a larger polygon
    const pointArray2Internal = data2.loopId !== 0;
    assert(() => isWindingExternal(poly1) !== pointArray1Internal, 'Winding order poly1 is incorrect');
    assert(() => isWindingExternal(poly2) !== pointArray2Internal, 'Winding order poly2 is incorrect');
    const loop1 = geoJsonExtractLoop(pointArray1, data1.vertId);
    const loop2 = geoJsonExtractLoop(pointArray2, data2.vertId);
    let bridgeStart = loop1[0];
    let bridgeEnd = loop2[0];
    const bridgeStartSlideTarget1 = loop1[loop1.length - 1];
    const bridgeStartSlideTarget2 = loop1[1];
    const bridgeEndSlideTarget1 = loop2[1];
    const bridgeEndSlideTarget2 = loop2[loop2.length - 1];
    // The polygons intersect at the point we wish to bridge them. We
    // can switch the position of the vertices in order to avoid
    // self-intersection
    // For more information see the documentation here:
    // https://carboncrop.atlassian.net/wiki/spaces/CAR/pages/137593025/Bridging+on+an+intersection
    if ((0, turf_1.inside)(bridgeStart, poly2) !== pointArray2Internal ||
        (0, turf_1.inside)(bridgeEnd, poly1) !== pointArray1Internal) {
        [bridgeStart, bridgeEnd] = [bridgeEnd, bridgeStart];
    }
    const bridgeWidth = (0, unit_conversion_1.metersToDegrees)(pointArray1[0], [bridgeWidthMeters, bridgeWidthMeters])[0];
    // Give bridge thickness by moving the points where the bridge goes between
    const bridgeStartPoint1 = calculateBridgeVertPosition(bridgeStart, bridgeEnd, bridgeStartSlideTarget1, bridgeWidth);
    const bridgeStartPoint2 = calculateBridgeVertPosition(bridgeStart, bridgeEnd, bridgeStartSlideTarget2, -bridgeWidth);
    const bridgeEndPoint1 = calculateBridgeVertPosition(bridgeEnd, bridgeStart, bridgeEndSlideTarget1, -bridgeWidth);
    const bridgeEndPoint2 = calculateBridgeVertPosition(bridgeEnd, bridgeStart, bridgeEndSlideTarget2, bridgeWidth);
    // Assembly a new loop containing both features and a bridge
    const coords = [
        bridgeStartPoint1,
        bridgeEndPoint1,
        ...loop2.slice(1, loop2.length),
        bridgeEndPoint2,
        bridgeStartPoint2,
        ...loop1.slice(1, loop1.length),
        bridgeStartPoint1,
    ];
    // Widen the bridge
    const coordsWalked = [bridgeStartPoint1, bridgeEndPoint2].reduce((coords, bridgeCoord) => {
        if (!coords) {
            return undefined;
        }
        // Because the index may change if there is a walk the other way, we instead find the
        // bridge based on the coordinates.
        // Unfortunately this also introduces an error condition. If the initial bridge width
        // causes two vertices to overlap, then both indexOf and indexOf+1 will be the same point
        // and the widening may fail depending on the BRIDGE_WALK_MAX_WIDTH_M and the distance to
        // neighboring points.
        // This is unimportant in production because BRIDGE_WALK_MAX_WIDTH is much larger than
        // the bridgeWidthMeters parameter
        const bridgeCoordIndex = coords === null || coords === void 0 ? void 0 : coords.indexOf(bridgeCoord);
        if (bridgeCoordIndex === -1) {
            return coords;
        }
        return walkBridge(coords, bridgeCoordIndex, BRIDGE_WALK_MAX_WIDTH_M);
    }, coords);
    const twoExternalRings = !pointArray1Internal && !pointArray2Internal;
    if (coordsWalked === undefined && twoExternalRings) {
        // Walking the bridge has removed the entire external hull! Let's not do any walking.
        return coords;
    }
    return coordsWalked;
}
/**
 * Creates a bridge between two polygons at the location specified.
 * The bridge width is determined by `APPROX_BRIDGE_WIDTH_METERS`
 *
 * @param geometry1 The geometry of one polygon
 * @param geometry2 The geometry of the other polygon
 * @param posData1 The indices to start the bridge at
 * @param posData2 The indices to end the bridge at
 * @param bridgeWidthMeters The approximate width of the bridge
 * @returns A single new geometry that includes both the original geometries.
 */
function bridgeSeparateGeometries(geometry1, geometry2, posData1, posData2, bridgeWidthMeters) {
    assert(() => isWindingExternal((0, turf_1.polygon)(geometry1)), 'Winding order geom1 is incorrect');
    assert(() => isWindingExternal((0, turf_1.polygon)(geometry2)), 'Winding order geom2 is incorrect');
    const loop1External = posData1.loopId === 0;
    const loop2External = posData2.loopId === 0;
    const joinedLoop = bridgeLoops(geometry1[posData1.loopId], geometry2[posData2.loopId], posData1, posData2, bridgeWidthMeters);
    const joinedLoops = joinedLoop ? [joinedLoop] : [];
    const remainingLoops1 = geometry1.filter((_, i) => i !== posData1.loopId);
    const remainingLoops2 = geometry2.filter((_, i) => i !== posData2.loopId);
    let outGeometry = [];
    if (loop1External && loop2External) {
        // If both our loops are external, our joined loop is the external loop
        if (joinedLoops.length === 0) {
            throw new Error('Walking exterior rings has completely removed them!');
        }
        outGeometry = [...joinedLoops, ...remainingLoops1, ...remainingLoops2];
    }
    else if (loop1External) {
        outGeometry = [...remainingLoops2, ...remainingLoops1, ...joinedLoops];
    }
    else if (loop2External) {
        outGeometry = [...remainingLoops1, ...remainingLoops2, ...joinedLoops];
    }
    else {
        throw new Error('Bridging between two internal loops. This cannot create valid output');
    }
    assert(() => isWindingExternal((0, turf_1.polygon)(outGeometry)), 'Winding order on output of bridgeSeparate is incorrect.');
    return outGeometry;
}
/**
 * Places a new vertex into a geojson geometry, respecting the fact that the first/last points
 * must be the same.
 *
 * @param geom The geometry you want to insert the point into
 * @param posInLoop what vertex/loop to place it at
 * @param newVertPosition the position in the world the new vertex should be located in
 */
function insertVertex(geom, posInLoop, newVertPosition) {
    const loop = geom[posInLoop.loopId];
    const vertId = posInLoop.vertId === loop.length ? 0 : posInLoop.vertId;
    loop.pop(); // Remove duplicate from end
    loop.splice(vertId, 0, newVertPosition);
    loop.push(loop[0]);
}
exports.insertVertex = insertVertex;
/**
 * Uses a graph to form bridges between polygons.
 * IMPORTANT: Bridging does not currently preserve canopy cover estimations
 *
 * @param inputFeatureCollection Create bridges between these features
 * @param graph Based on the edges present in this graph
 * @param bridgeWidthMeters The approximate width of the bridge
 * @returns A featureCollection of joined polygons
 */
function bridgePolygons(inputFeatureCollection, graph, bridgeWidthMeters) {
    // Because we are mutating features, we need to ensure we aren't messing other stages up
    // Note that this means we can't compare the feature stored in graph edges to items
    // in the `features` array.
    const features = lodash_1.default.cloneDeep(inputFeatureCollection);
    // Validate input data
    inputFeatureCollection.features.forEach((feature) => {
        if (!isWindingExternal(feature)) {
            throw Error('Poly Wind Invalid');
        }
    });
    const edgeJoinData = graph.edges.map((edge) => {
        // We need to change our edge references from the originals in the graph into
        // the ones in the features array
        const startFeature = features.features[inputFeatureCollection.features.indexOf(edge.featureA)];
        const endFeature = features.features[inputFeatureCollection.features.indexOf(edge.featureB)];
        const joinData = edge.connectionData;
        // Grab vertex positions rather than joinData.position (which can include positions along edges
        // rather than at a vertex)
        const startVertPos = startFeature.geometry.coordinates[joinData.ringIndexA][joinData.vertexIndexA];
        const endVertPos = endFeature.geometry.coordinates[joinData.ringIndexB][joinData.vertexIndexB];
        return {
            startFeature: startFeature,
            startVertPos: startVertPos,
            endVertPos: endVertPos,
            endFeature: endFeature,
            idealStartPosition: edge.connectionData.positionA,
            idealEndPosition: edge.connectionData.positionB,
            distance: edge.connectionData.distance,
        };
    });
    // Sorting bridges by distance means that shorter bridges are completed first.
    // Because we don't allow multiple bridges between the same feature this prevents
    // bridges from overlapping existing geometry. For more information see the images
    // associated with issue CAR-1072.
    edgeJoinData.sort((a, b) => a.distance - b.distance);
    // As we go through we remove polygons. We need some way of identifying where they go to.
    const featureRemap = new Map();
    const deleteFeatures = [];
    edgeJoinData.forEach((edgeData) => {
        const realStartFeature = featureRemap.get(edgeData.startFeature) || edgeData.startFeature;
        const realEndFeature = featureRemap.get(edgeData.endFeature) || edgeData.endFeature;
        const startData = findPolyLoopAndRingIndex(realStartFeature, edgeData.startVertPos);
        const endData = findPolyLoopAndRingIndex(realEndFeature, edgeData.endVertPos);
        // If multiple bridges are made at the same location, it may not be able to find the place
        // to create the new bridge
        if (startData !== undefined && endData !== undefined) {
            // Create a bridge
            const startGeom = realStartFeature.geometry.coordinates;
            const endGeom = realEndFeature.geometry.coordinates;
            const startFeatureArea = (0, turf_1.area)(realStartFeature);
            const endFeatureArea = (0, turf_1.area)(realEndFeature);
            const initialTotalArea = startFeatureArea + endFeatureArea;
            if (realStartFeature !== realEndFeature) {
                // We are bridging between two different features
                try {
                    // Now that we know approximately where the bridge should be, we can now check
                    // to see if we are bridging to an edge. If so, we can create an additional
                    // vertex in our polygon
                    if (!pointExactlyEqual(edgeData.idealStartPosition, edgeData.startVertPos)) {
                        startData.vertId += 1;
                        insertVertex(startGeom, startData, edgeData.idealStartPosition);
                    }
                    if (!pointExactlyEqual(edgeData.idealEndPosition, edgeData.endVertPos)) {
                        endData.vertId += 1;
                        insertVertex(endGeom, endData, edgeData.idealEndPosition);
                    }
                    const outGeometry = bridgeSeparateGeometries(startGeom, endGeom, startData, endData, bridgeWidthMeters);
                    realStartFeature.geometry.coordinates = outGeometry;
                    const finalArea = (0, turf_1.area)(realStartFeature);
                    const areaChange = finalArea - initialTotalArea; // Area grow = positive
                    if (areaChange < -MAX_BRIDGE_REDUCE_AREA_SQM) {
                        // If bridging creates a twist or an interior ring escapes, the area reduces.
                        // There is no case where a valid bridge will reduce the total area, so this is
                        // clearly a problem. For now we just log the error and revert to what it was
                        // before. This can leave some overlaps or unjoined polygons.
                        console.error(`Bridging two features would have reduced area by ${areaChange}sqm`);
                        realStartFeature.geometry.coordinates = startGeom;
                    }
                    else {
                        // One feature gets the geometry of both, so we can get rid of the other one
                        deleteFeatures.push(realEndFeature);
                        // We've just joined start feature to end feature, so we need to
                        // tell future bridge operations where to go to.
                        featureRemap.set(realEndFeature, realStartFeature);
                        featureRemap.forEach((value, key) => {
                            if (value === realEndFeature) {
                                featureRemap.set(key, realStartFeature);
                            }
                        });
                    }
                }
                catch (error) {
                    // In case of exception, leave unjoined polygons.
                    console.error(`Bridging two features failed: ${error}`);
                    realStartFeature.geometry.coordinates = startGeom;
                }
            }
        }
    });
    const remainingFeatures = features.features.filter((f) => !deleteFeatures.includes(f));
    return (0, turf_1.featureCollection)(remainingFeatures);
}
exports.bridgePolygons = bridgePolygons;
